import React, { useId } from 'react';

export type FrameType = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';

interface AvatarFrameProps {
  frame: FrameType;
  avatarUrl?: string;
  size?: number;
  className?: string;
}

// Default avatar as inline SVG data URI (no external file dependency)
const DEFAULT_AVATAR = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" rx="40" fill="#e2e8f0"/><circle cx="40" cy="30" r="14" fill="#94a3b8"/><ellipse cx="40" cy="62" rx="22" ry="16" fill="#94a3b8"/></svg>'
)}`;

// Frame color configurations
const FRAME_COLORS: Record<FrameType, {
  primary: string;
  secondary: string;
  glow: string;
  shine: string;
}> = {
  none: {
    primary: 'transparent',
    secondary: 'transparent',
    glow: 'transparent',
    shine: 'transparent',
  },
  bronze: {
    primary: '#CD7F32',
    secondary: '#8B4513',
    glow: '#DDA15E',
    shine: '#F4A460',
  },
  silver: {
    primary: '#C0C0C0',
    secondary: '#808080',
    glow: '#E8E8E8',
    shine: '#FFFFFF',
  },
  gold: {
    primary: '#FFD700',
    secondary: '#DAA520',
    glow: '#FFE55C',
    shine: '#FFFACD',
  },
  platinum: {
    primary: '#E5E4E2',
    secondary: '#A9A9A9',
    glow: '#F5F5F5',
    shine: '#FFFFFF',
  },
  diamond: {
    primary: '#B9F2FF',
    secondary: '#87CEEB',
    glow: '#E0FFFF',
    shine: '#FFFFFF',
  },
};

export const AvatarFrame: React.FC<AvatarFrameProps> = ({
  frame,
  avatarUrl,
  size = 80,
  className = '',
}) => {
  const uniqueId = useId();
  const colors = FRAME_COLORS[frame];
  const frameWidth = size * 0.08;
  const innerSize = size - frameWidth * 2;

  if (frame === 'none') {
    return (
      <div
        className={className}
        style={{ position: 'relative', display: 'inline-block', width: size, height: size }}
      >
        <img
          src={avatarUrl || DEFAULT_AVATAR}
          alt="Avatar"
          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ position: 'relative', display: 'inline-block', width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <defs>
          {/* Gradient for frame */}
          <linearGradient id={`frame-gradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.shine} />
            <stop offset="30%" stopColor={colors.primary} />
            <stop offset="70%" stopColor={colors.secondary} />
            <stop offset="100%" stopColor={colors.primary} />
          </linearGradient>

          {/* Glow filter */}
          <filter id={`glow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Clip path for avatar */}
          <clipPath id={`avatar-clip-${uniqueId}`}>
            <circle cx={size / 2} cy={size / 2} r={innerSize / 2} />
          </clipPath>

          {/* Diamond sparkle pattern */}
          {frame === 'diamond' && (
            <>
              <pattern id={`diamond-sparkle-${uniqueId}`} patternUnits="userSpaceOnUse" width="20" height="20">
                <circle cx="10" cy="10" r="1" fill="#FFFFFF" opacity="0.8">
                  <animate attributeName="opacity" values="0.8;0.2;0.8" dur="2s" repeatCount="indefinite" />
                </circle>
              </pattern>
              {/* Rainbow gradient for shimmer effect */}
              <linearGradient id={`diamond-rainbow-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FF6B6B" />
                <stop offset="16%" stopColor="#FFE66D" />
                <stop offset="33%" stopColor="#4ECDC4" />
                <stop offset="50%" stopColor="#45B7D1" />
                <stop offset="66%" stopColor="#96E6FF" />
                <stop offset="83%" stopColor="#DDA0DD" />
                <stop offset="100%" stopColor="#FF6B6B" />
              </linearGradient>
              {/* Enhanced glow filter for diamond */}
              <filter id={`diamond-glow-${uniqueId}`} x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </>
          )}
        </defs>

        {/* Outer glow for premium frames */}
        {(frame === 'gold' || frame === 'platinum' || frame === 'diamond') && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size / 2 - 2}
            fill="none"
            stroke={colors.glow}
            strokeWidth="4"
            opacity="0.5"
            filter={`url(#glow-${uniqueId})`}
          />
        )}

        {/* Main frame ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 2 - frameWidth / 2}
          fill="none"
          stroke={`url(#frame-gradient-${uniqueId})`}
          strokeWidth={frameWidth}
        />

        {/* Inner border */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={innerSize / 2 + 1}
          fill="none"
          stroke={colors.secondary}
          strokeWidth="1"
          opacity="0.5"
        />

        {/* Outer border */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 2 - 1}
          fill="none"
          stroke={colors.shine}
          strokeWidth="1"
          opacity="0.3"
        />

        {/* Diamond frame special effects */}
        {frame === 'diamond' && (
          <>
            {/* Rainbow shimmer overlay */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2 - frameWidth / 2}
              fill="none"
              stroke={`url(#diamond-rainbow-${uniqueId})`}
              strokeWidth={frameWidth * 0.6}
              opacity="0.5"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`0 ${size / 2} ${size / 2}`}
                to={`360 ${size / 2} ${size / 2}`}
                dur="4s"
                repeatCount="indefinite"
              />
            </circle>

            {/* Bright sparkle points */}
            <g>
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                <g key={angle}>
                  {/* Large sparkle */}
                  <circle
                    cx={size / 2 + Math.cos((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2)}
                    cy={size / 2 + Math.sin((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2)}
                    r="4"
                    fill="#FFFFFF"
                    filter={`url(#glow-diamond)`}
                  >
                    <animate
                      attributeName="r"
                      values="3;5;3"
                      dur={`${0.8 + i * 0.15}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="1;0.5;1"
                      dur={`${0.8 + i * 0.15}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                  {/* Sparkle rays */}
                  <line
                    x1={size / 2 + Math.cos((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2 - 6)}
                    y1={size / 2 + Math.sin((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2 - 6)}
                    x2={size / 2 + Math.cos((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2 + 6)}
                    y2={size / 2 + Math.sin((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2 + 6)}
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    opacity="0.8"
                  >
                    <animate
                      attributeName="opacity"
                      values="0.8;0.2;0.8"
                      dur={`${0.8 + i * 0.15}s`}
                      repeatCount="indefinite"
                    />
                  </line>
                </g>
              ))}
            </g>
          </>
        )}

        {/* Platinum frame shine effect */}
        {frame === 'platinum' && (
          <>
            {/* Pulsing outer glow */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2 + 1}
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="3"
              opacity="0.5"
            >
              <animate
                attributeName="opacity"
                values="0.5;0.2;0.5"
                dur="2.5s"
                repeatCount="indefinite"
              />
            </circle>

            {/* Fast rotating bright arc */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2 - frameWidth / 2}
              fill="none"
              stroke="#FFFFFF"
              strokeWidth={frameWidth * 0.5}
              strokeDasharray={`${size * 0.15} ${size * 0.85}`}
              strokeLinecap="round"
              opacity="0.9"
              filter={`url(#glow-platinum)`}
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`0 ${size / 2} ${size / 2}`}
                to={`360 ${size / 2} ${size / 2}`}
                dur="3s"
                repeatCount="indefinite"
              />
            </circle>

            {/* Secondary rotating arc (opposite direction) */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={size / 2 - frameWidth / 2}
              fill="none"
              stroke="#E8E8E8"
              strokeWidth={frameWidth * 0.3}
              strokeDasharray={`${size * 0.1} ${size * 0.9}`}
              strokeLinecap="round"
              opacity="0.7"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`360 ${size / 2} ${size / 2}`}
                to={`0 ${size / 2} ${size / 2}`}
                dur="5s"
                repeatCount="indefinite"
              />
            </circle>

            {/* Bright highlight spots */}
            {[0, 120, 240].map((angle, i) => (
              <circle
                key={angle}
                cx={size / 2 + Math.cos((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2)}
                cy={size / 2 + Math.sin((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2)}
                r="3"
                fill="#FFFFFF"
                filter={`url(#glow-platinum)`}
              >
                <animate
                  attributeName="opacity"
                  values="1;0.4;1"
                  dur={`${1.2 + i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </>
        )}

        {/* Gold frame decorative elements */}
        {frame === 'gold' && (
          <>
            {[0, 90, 180, 270].map((angle) => (
              <circle
                key={angle}
                cx={size / 2 + Math.cos((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2)}
                cy={size / 2 + Math.sin((angle * Math.PI) / 180) * (size / 2 - frameWidth / 2)}
                r="3"
                fill={colors.shine}
              />
            ))}
          </>
        )}
      </svg>

      {/* Avatar image */}
      <div
        style={{
          position: 'absolute',
          borderRadius: '50%',
          overflow: 'hidden',
          top: frameWidth,
          left: frameWidth,
          width: innerSize,
          height: innerSize,
        }}
      >
        <img
          src={avatarUrl || DEFAULT_AVATAR}
          alt="Avatar"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    </div>
  );
};

export default AvatarFrame;
